import type React from 'react'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'
import { connectorPathD, inPortPoint, outPortPoint } from '@/lib/text-linking/text-linking-geometry'

import { chromeFrameOf } from '../helpers/chrome-frame-of'
import { PortSquare } from './port-square'

/**
 * Connectors + passive numbered in-ports, one decorative group per chain. Frames already shown
 * interactively by the layer's port passes (`portFrameIds`, and `lockedOutPortFrameIds` for a
 * head's out-port) are skipped here to avoid rendering the same port twice.
 */
export function ChainConnectors({
  chains,
  scale,
  portNumberOffset,
  portFrameIds,
  lockedOutPortFrameIds,
  seqByFrameId,
}: {
  chains: LinkableTextFrame[][]
  scale: number
  portNumberOffset: number
  portFrameIds: Set<string>
  lockedOutPortFrameIds: Set<string>
  seqByFrameId: Map<string, number>
}): React.JSX.Element {
  return (
    <>
      {chains.map((chain) => {
        const segments = []

        for (let i = 0; i < chain.length - 1; i++) {
          const fromChrome = chromeFrameOf(chain[i]!, scale)
          const toChrome = chromeFrameOf(chain[i + 1]!, scale)
          const from = outPortPoint(fromChrome, portNumberOffset, fromChrome.pivot)
          const to = inPortPoint(toChrome, portNumberOffset, toChrome.pivot)

          segments.push(
            <path
              key={`${chain[i]!.id}-${chain[i + 1]!.id}`}
              d={connectorPathD(from, to)}
              fill="none"
              stroke="#3B82F6"
              strokeWidth={1.5}
              strokeDasharray="3 3"
            />
          )
        }

        const passiveInPorts = chain
          .slice(1)
          .filter((f) => !portFrameIds.has(f.id))
          .map((f) => {
            const chrome = chromeFrameOf(f, scale)

            return (
              <PortSquare
                key={`passive-in-${f.id}`}
                center={inPortPoint(chrome, portNumberOffset, chrome.pivot)}
                rotationDeg={f.rotation ?? 0}
                seq={seqByFrameId.get(f.id)}
                interactive={false}
              />
            )
          })

        // Chain head's out-port badge ("1") — mirrors passiveInPorts above so the first frame
        // gets the same passive numbering as the rest when only shown via the View toggle.
        const head = chain[0]!
        const headChrome = chromeFrameOf(head, scale)
        const passiveOutPort = !portFrameIds.has(head.id) &&
          !lockedOutPortFrameIds.has(head.id) && (
            <PortSquare
              key={`passive-out-${head.id}`}
              center={outPortPoint(headChrome, portNumberOffset, headChrome.pivot)}
              rotationDeg={head.rotation ?? 0}
              seq={seqByFrameId.get(head.id)}
              interactive={false}
              portRole="out"
            />
          )

        // Wholly decorative: connectors plus passive badges, nothing focusable inside.
        return (
          <g key={chain.map((f) => f.id).join('-')} aria-hidden="true">
            {segments}
            {passiveOutPort}
            {passiveInPorts}
          </g>
        )
      })}
    </>
  )
}
